import React, {useEffect, useRef, useState} from "react";
import SpeechRecognition, {useSpeechRecognition} from "react-speech-recognition";
import axios from "axios";
import Swal from "sweetalert2";
import {Button, Card, Form, InputGroup, OverlayTrigger, Spinner, Tooltip} from "react-bootstrap";
import ChatWindow from "./ChatWindow";
import {baseUrl, userLocation, userName} from "../Univ";

// Put this file in the same folder as AIRegister.jsx (it reuses ./ChatWindow).
// Add a route, e.g.  <Route path="/ai-assistant" element={<AiAssistant/>}/>

const WELCOME = {
    ta: "வணக்கம்! 👋 நான் DrHiS AI உதவியாளர். பார்மசி விற்பனை, கொள்முதல், லாபம், ஸ்டாக், காலாவதி மற்றும் சப்ளையர் நிலுவை பற்றி கேளுங்கள்.",
    en: "Hello! 👋 I am the DrHiS AI assistant. Ask me about pharmacy sales, purchases, profit, stock, expiry and supplier dues.",
};

const QUICK = [
    {ta: "இன்றைய பார்மசி summary", en: "Pharmacy summary today"},
    {ta: "இந்த மாத பார்மசி summary", en: "Pharmacy summary this month"},
    {ta: "குறைந்த ஸ்டாக் மருந்துகள்", en: "Low stock medicines"},
    {ta: "காலாவதியாகும் மருந்துகள்", en: "Expiring medicines"},
    {ta: "சப்ளையர் நிலுவை", en: "Pending supplier payments"},
];

export default function AiAssistant() {
    const [lang, setLang] = useState("ta");
    const [messages, setMessages] = useState([{sender: "bot", text: WELCOME.ta}]);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [voiceEnabled, setVoiceEnabled] = useState(false);
    const [speaking, setSpeaking] = useState(false);

    const chatContainerRef = useRef(null);
    const wasListening = useRef(false);

    const {transcript, listening, resetTranscript, browserSupportsSpeechRecognition} = useSpeechRecognition();

    const speechLang = lang === "ta" ? "ta-IN" : "en-IN";

    // ---------------------------------------------------------------- speech out
    const cleanSpeech = (text) =>
        (text || "").replace(/[*#_`]/g, "").replace(/\n+/g, ". ").replace(/\s+/g, " ").trim();

    const stopSpeaking = () => {
        window.speechSynthesis.cancel();
        setSpeaking(false);
    };

    const speak = (text) => {
        if (!voiceEnabled || !text) return;
        const clean = cleanSpeech(text);
        if (!clean) return;
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(clean);
        u.lang = speechLang;
        u.onstart = () => setSpeaking(true);
        u.onend = () => setSpeaking(false);
        u.onerror = () => setSpeaking(false);
        window.speechSynthesis.resume();
        setTimeout(() => window.speechSynthesis.speak(u), 100);
    };

    // ---------------------------------------------------------------- send
    const sendMessage = async (voiceMessage = null) => {
        const question = (typeof voiceMessage === "string" ? voiceMessage : message).trim();
        if (!question || loading) return;

        const history = messages.slice(1).map((m) => ({
            role: m.sender === "user" ? "user" : "assistant",
            content: m.text,
        }));

        setMessages((prev) => [...prev, {sender: "user", text: question}]);
        setMessage("");
        setLoading(true);

        try {
            const response = await axios.post(
                baseUrl + "hospital-ai/chat/",
                {
                    message: question,
                    history,
                    language: lang,
                    username: userName,
                    location: userLocation || 1,
                },
                {timeout: 300000} // first answer can take 30-60 s on a CPU-only machine
            );
            const reply = response.data.reply || "No response.";
            setMessages((prev) => [...prev, {sender: "bot", text: reply}]);
            speak(reply);
        } catch (error) {
            console.log("AI ASSISTANT ERROR", error);
            const text =
                error?.response?.data?.reply ||
                (error?.code === "ECONNABORTED"
                    ? "⏳ The AI took too long. Please try again."
                    : "❌ Sorry, I couldn't process your request.");
            setMessages((prev) => [...prev, {sender: "bot", text}]);
        } finally {
            setLoading(false);
        }
    };

    // ---------------------------------------------------------------- mic
    const micClicked = () => {
        if (!browserSupportsSpeechRecognition) {
            Swal.fire({icon: "warning", title: "Voice input", text: "Please use Chrome or Edge for voice input."});
            return;
        }
        if (listening) {
            SpeechRecognition.stopListening();
            return;
        }
        stopSpeaking();
        resetTranscript();
        SpeechRecognition.startListening({continuous: false, language: speechLang});
    };

    // send automatically when the microphone stops and something was heard
    useEffect(() => {
        if (wasListening.current && !listening && transcript.trim()) {
            sendMessage(transcript.trim());
            resetTranscript();
        }
        wasListening.current = listening;
    }, [listening]);

    // ---------------------------------------------------------------- misc
    const changeLanguage = (value) => {
        setLang(value);
        // refresh the greeting only if the conversation has not started
        setMessages((prev) => (prev.length === 1 ? [{sender: "bot", text: WELCOME[value]}] : prev));
    };

    const clearChat = () => {
        stopSpeaking();
        setMessages([{sender: "bot", text: WELCOME[lang]}]);
    };

    const toggleVoice = () => {
        if (voiceEnabled) stopSpeaking();
        setVoiceEnabled(!voiceEnabled);
    };

    useEffect(() => {
        const c = chatContainerRef.current;
        if (c) c.scrollTo({top: c.scrollHeight, behavior: "smooth"});
    }, [messages, loading]);

    useEffect(() => {
        document.title = "AI Assistant";
        return () => window.speechSynthesis.cancel();
    }, []);

    // ---------------------------------------------------------------- render
    return (
        <div className="container-fluid py-3">
            <div className="row justify-content-center">
                <div className="col-lg-9">
                    <Card className="shadow-lg border-0 rounded-4">
                        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                            <strong>🤖 DrHiS AI Assistant</strong>

                            <div className="d-flex align-items-center gap-2">
                                {loading && (
                                    <span className="badge bg-warning text-dark">
                                        <Spinner animation="border" size="sm" className="me-2"/>
                                        Thinking...
                                    </span>
                                )}
                                {!loading && speaking && <span className="badge bg-success">🔊 Speaking</span>}
                                {!loading && !speaking && listening && (
                                    <span className="badge bg-danger">🎤 Listening</span>
                                )}

                                <Form.Select
                                    size="sm"
                                    value={lang}
                                    onChange={(e) => changeLanguage(e.target.value)}
                                    style={{width: 110}}
                                >
                                    <option value="ta">தமிழ்</option>
                                    <option value="en">English</option>
                                </Form.Select>

                                <Button size="sm" variant="light" onClick={clearChat}>
                                    <i className="fa fa-trash me-1"></i>Clear
                                </Button>
                            </div>
                        </Card.Header>

                        <Card.Body>
                            <div
                                ref={chatContainerRef}
                                style={{height: "55vh", overflowY: "auto", paddingRight: "8px"}}
                            >
                                <ChatWindow messages={messages}/>
                            </div>

                            <div className="d-flex flex-wrap gap-2 mt-3">
                                {QUICK.map((q) => (
                                    <Button
                                        key={q.en}
                                        size="sm"
                                        variant="outline-primary"
                                        disabled={loading}
                                        onClick={() => sendMessage(q[lang])}
                                    >
                                        {q[lang]}
                                    </Button>
                                ))}
                            </div>

                            <InputGroup className="mt-3">
                                <Form.Control
                                    placeholder={lang === "ta" ? "உங்கள் கேள்வியை தட்டச்சு செய்யவும்..." : "Type your question..."}
                                    value={listening ? transcript : message}
                                    disabled={loading}
                                    autoComplete="off"
                                    onChange={(e) => setMessage(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            sendMessage();
                                        }
                                    }}
                                />

                                <Button variant="primary" disabled={loading} onClick={() => sendMessage()}>
                                    <i className="fa fa-paper-plane"></i>
                                </Button>

                                <OverlayTrigger placement="top" overlay={<Tooltip>Voice Input</Tooltip>}>
                                    <Button variant={listening ? "danger" : "success"} disabled={loading}
                                            onClick={micClicked}>
                                        <i className="fa fa-microphone"></i>
                                    </Button>
                                </OverlayTrigger>

                                <OverlayTrigger placement="top" overlay={<Tooltip>Speak answers</Tooltip>}>
                                    <Button variant={voiceEnabled ? "info" : "secondary"} onClick={toggleVoice}>
                                        <i className={voiceEnabled ? "fa fa-volume-up" : "fa fa-volume-off"}/>
                                    </Button>
                                </OverlayTrigger>
                            </InputGroup>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
}
